import './FilePreview.scss';

import {
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { Empty, Modal, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import FileData, { FILE_KIND } from '@/model/General/FileData';

interface FilePreviewProps {
  file: FileData;
  size?: number;
  showOpenButton?: boolean;
  mediaFit?: 'cover' | 'contain' | 'fill';
  canDownload?: boolean;
  actions?: React.ReactNode;
}

export default function FilePreview({
  file,
  size = 150,
  mediaFit = 'cover',
  actions,
}: FilePreviewProps) {
  const [openModal, setOpenModal] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | undefined>();
  const src = file.blobFile ? objectUrl : file.fileUrl;
  const kind: FILE_KIND = useMemo(() => file.getFileKind(), [file]);

  useEffect(() => {
    if (file.blobFile) {
      const url = URL.createObjectURL(file.blobFile);

      setObjectUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [file.blobFile]);

  const mediaContent = useMemo(() => {
    const mediaStyle: React.CSSProperties = {
      objectFit: mediaFit,
    };

    return kind === FILE_KIND.IMAGE ? (
      <img className="FilePreviewMedia" src={src} alt={file.title} style={mediaStyle} />
    ) : kind === FILE_KIND.VIDEO ? (
      <span className="FilePreviewMediaIcon">
        <VideoCameraOutlined />
      </span>
    ) : kind === FILE_KIND.AUDIO ? (
      <span className="FilePreviewMediaIcon">
        <PlayCircleOutlined />
      </span>
    ) : kind === FILE_KIND.PDF ? (
      <span className="FilePreviewMediaIcon">
        <FilePdfOutlined /> PDF
      </span>
    ) : kind === FILE_KIND.TEXT ? (
      <span className="FilePreviewMediaIcon">
        <FileTextOutlined />
      </span>
    ) : (
      kind === FILE_KIND.OTHER && (
        <span className="FilePreviewMediaIcon">
          <FileOutlined />
        </span>
      )
    );
  }, [file.title, kind, mediaFit, src]);

  if (!src) return <Empty description="" style={{ width: size, height: size }} />;

  return (
    <>
      <div
        className="FilePreview"
        style={{ width: size, height: size }}
        onClick={() => setOpenModal(true)}>
        <div className={`FilePreviewTitle ${file.removed ? 'FilePreviewTitleRemoved' : ''}`}>
          <Tooltip title={file.title}>
            <p>{file.title}</p>
          </Tooltip>
        </div>
        {mediaContent}
        {actions && (
          <div
            className="FilePreviewActions"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            {actions}
          </div>
        )}
        {file.removed && <div className="FilePreviewRemoved" />}
      </div>
      <Modal
        centered
        className="FilePreviewModal"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}>
        {mediaContent}
      </Modal>
    </>
  );
}
